import { FC } from 'react';
import Typography from '@mui/material/Typography';
import { Schema as AccountSchema } from 'further-types/lp/account';
import { CalendarDay } from 'further-ui/utils';
import { Column, Row } from 'further-ui/components/Layout';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import PageLoader from 'components/PageLoader';
import FullHeightView from 'components/new/FullHeightView';
import PageFilters, { Filter } from 'components/new/PageFilters';
import { useFilters, usePagination } from 'further-ui/hooks';
import SearchField from 'further-ui/components/SearchField';
import { useLayoutContext } from 'contexts/LayoutContext';
import { SyncScrollProvider } from 'contexts/SyncScrollContext';
import DatePicker from 'further-ui/components/DatePicker';
import ActionButtons from 'further-ui/components/ActionButtons';
import api from 'lib/trpcClient';
import downloadXlxs from 'utils/downloadXlxs';
import GeneralLedgerTotal from './GeneralLedgerTotal';
import SectionContent from './SectionContent';
import {
  getLastFinancialYearStartDate,
  buildDateRangeSubtitle,
} from './helpers';
import { ApiInputs, ApiResponses } from 'lib/trpcClient';

const { AccountSection } = AccountSchema;

const pageId = 'general-ledger';

const GeneralLedger: FC = () => {
  const { vehicle, selectedVehicleId } = useLayoutContext();
  const todayDate = new Date();
  const today = new CalendarDay(todayDate);

  const lastFinancialYearStartDate = getLastFinancialYearStartDate(
    vehicle,
    todayDate,
  );

  const { filters, handleChangeFilters } = useFilters<{
    startDate?: CalendarDay;
    endDate?: CalendarDay;
    searchQuery?: string;
  }>(pageId, {
    defaultFilters: {
      startDate: lastFinancialYearStartDate,
      endDate: today,
      searchQuery: '',
    },
  });
  const pagination = usePagination({
    id: pageId,
    order: 'desc',
    orderBy: 'transactionDate',
  });

  const handleStartDateChange = (date: CalendarDay | null) => {
    const newStartDate = date ?? undefined;
    const isAfterEndDate =
      !!newStartDate &&
      !!filters.endDate &&
      newStartDate.isAfter(filters.endDate);

    handleChangeFilters(
      isAfterEndDate
        ? {
            startDate: filters.endDate,
            endDate: newStartDate,
          }
        : {
            startDate: newStartDate,
          },
    );
  };

  const handleEndDateChange = (date: CalendarDay | null) => {
    const newEndDate = date ?? undefined;
    const isBeforeStartDate =
      filters.startDate && newEndDate?.isBefore(filters.startDate);

    handleChangeFilters(
      isBeforeStartDate
        ? {
            startDate: newEndDate,
            endDate: filters.startDate,
          }
        : {
            endDate: newEndDate,
          },
    );
  };

  const hasRequiredParams = !!filters.endDate && !!selectedVehicleId;

  const { data, isLoading, isFetching, refetch } =
    api.generalLedger.listTransactions.useQuery(
      {
        ...filters,
        startDate: filters.startDate,
        endDate: filters.endDate ?? today,
        orderBy: pagination.orderBy,
        order: pagination.order,
        vehicleId: selectedVehicleId ?? '',
      },
      { enabled: hasRequiredParams },
    );

  const sections = data?.sections ?? {};

  const exportTransactions = api.generalLedger.exportTransactions.useMutation(
    useHandleTrpcMutation<
      ApiResponses['generalLedger']['exportTransactions'],
      ApiInputs['generalLedger']['exportTransactions']
    >()({
      onSuccess: (data, variables) => {
        downloadXlxs(
          data.file,
          `General ledger - ${
            vehicle?.vehicleName
          } - Up to ${variables.endDate.toLabel()}`,
        );
      },
      invalidations: [],
    }),
  );

  if (isLoading || !vehicle) {
    return <PageLoader />;
  }

  const handleExport = () => {
    if (!filters.endDate) return;
    exportTransactions.mutate({
      ...filters,
      startDate: filters.startDate,
      endDate: filters.endDate,
      vehicleId: selectedVehicleId ?? '',
    });
  };

  const dateRangeSubtitle = buildDateRangeSubtitle(
    vehicle.vehicleName,
    filters.startDate,
    filters.endDate,
    lastFinancialYearStartDate,
    today,
  );

  return (
    <SyncScrollProvider>
      <FullHeightView
        actionBar={
          <GeneralLedgerTotal
            total={data?.total ?? 0}
            baseCurrency={vehicle.baseCurrency}
          />
        }
      >
        <Column spacing="lg">
          <Row
            spacing="sm"
            justify="space-between"
            alignItems="flex-end"
            breakpoint="sm"
          >
            <Column spacing="xs">
              <Typography variant="h5" color="grey.900">
                General ledger
              </Typography>
              <Typography variant="body2">{dateRangeSubtitle}</Typography>
            </Column>
            <ActionButtons
              actions={[
                {
                  label: 'Update',
                  variant: 'outlined',
                  onClick: refetch,
                  disabled: !filters.endDate,
                },
                {
                  label: 'Export',
                  variant: 'contained',
                  onClick: handleExport,
                  disabled: !filters.endDate,
                },
              ]}
            />
          </Row>
          <PageFilters align="flex-start">
            <Filter>
              <DatePicker
                fullWidth
                value={filters.startDate}
                onChange={handleStartDateChange}
                placeholder="Select start date"
                clearable
              />
            </Filter>
            <Filter>
              <DatePicker
                fullWidth
                value={filters.endDate}
                onChange={handleEndDateChange}
                placeholder="Select end date"
                required
                error={!filters.endDate}
                helperText={
                  !filters.endDate ? 'End date is required.' : undefined
                }
              />
            </Filter>
            <Filter>
              <SearchField
                autoSearchOnDebounce
                name="searchQuery"
                placeholder="Search by counterparty, description or author"
                fullWidth
                value={filters.searchQuery ?? ''}
                onSearch={(searchQuery) => {
                  handleChangeFilters({ searchQuery });
                }}
                allowEmptyQuery
              />
            </Filter>
          </PageFilters>
          <SectionContent
            sectionTitle="Assets"
            section={sections[AccountSection.Assets]}
            isLoading={isFetching}
            baseCurrency={vehicle.baseCurrency}
          />
          <SectionContent
            sectionTitle="Payables"
            section={sections[AccountSection.Payables]}
            isLoading={isFetching}
            baseCurrency={vehicle.baseCurrency}
          />
          <SectionContent
            sectionTitle="Partners' capital"
            section={sections[AccountSection.PartnersCapital]}
            isLoading={isFetching}
            baseCurrency={vehicle.baseCurrency}
          />
          <SectionContent
            sectionTitle="Income"
            section={sections[AccountSection.Income]}
            isLoading={isFetching}
            baseCurrency={vehicle.baseCurrency}
          />
          <SectionContent
            sectionTitle="Expenses"
            section={sections[AccountSection.Expenses]}
            isLoading={isFetching}
            baseCurrency={vehicle.baseCurrency}
          />
        </Column>
      </FullHeightView>
    </SyncScrollProvider>
  );
};

export default GeneralLedger;
